import {
  coreExtensionData,
  createFrontendModule,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { Content } from '@backstage/core-components';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import {
  CatalogTable,
  type CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import { Grid } from '@backstage/ui';
import catalogAlphaPlugin, {
  CatalogIndexPage,
} from '@backstage/plugin-catalog/alpha';
import techdocsAlphaPlugin from '@backstage/plugin-techdocs/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import qs from 'qs';
import { CustomFilters } from '../components/catalog/CustomCatalogPage';

// Add an External Dependencies tab to the DevTools plugin
const devToolsExternalDependenciesPage = SubPageBlueprint.make({
  name: 'external-dependencies',
  params: {
    path: 'external-dependencies',
    title: 'External Dependencies',
    loader: () =>
      import('@backstage/plugin-devtools').then(m => (
        <Content>
          <m.ExternalDependenciesContent />
        </Content>
      )),
  },
});

export const devtoolsExtraPages = createFrontendModule({
  pluginId: 'devtools',
  extensions: [devToolsExternalDependenciesPage],
});

export const customTechdocsPlugin: typeof techdocsAlphaPlugin =
  techdocsAlphaPlugin.withOverrides({
    extensions: [
      techdocsAlphaPlugin.getExtension('entity-content:techdocs').override({
        factory(originalFactory, context) {
          return originalFactory({
            config: {
              ...context.config,
              filter: {
                kind: { $in: ['component', 'group'] },
              },
            },
          });
        },
      }),
    ],
  });

export const customApiDocsPlugin: typeof apiDocsPlugin =
  apiDocsPlugin.withOverrides({
    extensions: [
      apiDocsPlugin.getExtension('entity-content:api-docs/apis').override({
        factory(originalFactory, context) {
          return originalFactory({
            config: {
              ...context.config,
              path: 'api',
              title: 'API',
              filter: 'kind:component,spec.type=service',
            },
          });
        },
      }),
    ],
  });

const myColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const params = qs.parse(global.window.location.search, {
    ignoreQueryPrefix: true,
  }).columns;
  if (
    typeof params === 'object' &&
    !Array.isArray(params) &&
    Array.isArray(params.field) &&
    Array.isArray(params.title)
  ) {
    const fields = params.field as string[];
    const titles = params.title as string[];
    const newColumns = fields.map((column, index) => {
      switch (column) {
        case 'resolved.name':
          return CatalogTable.columns.createNameColumn();
        case 'resolved.partOfSystemRelationTitle':
          return CatalogTable.columns.createSystemColumn();
        case 'resolved.ownedByRelationsTitle':
          return CatalogTable.columns.createOwnerColumn();
        case 'entity.metadata.description':
          return CatalogTable.columns.createMetadataDescriptionColumn();
        case 'entity.metadata.tags':
          return CatalogTable.columns.createTagsColumn();
        default:
          return {
            field: column,
            title: titles[index],
          };
      }
    });
    return newColumns;
  }

  return CatalogTable.defaultColumnsFunc(entityListContext);
};

export const customCatalogPlugin: typeof catalogAlphaPlugin =
  catalogAlphaPlugin.withOverrides({
    extensions: [
      catalogAlphaPlugin.getExtension('page:catalog').override({
        factory(originalFactory) {
          return originalFactory({
            params: {
              loader: async () => (
                <CatalogIndexPage
                  columns={myColumnsFunc}
                  filters={<CustomFilters />}
                />
              ),
            },
          });
        },
      }),
    ],
  });

// `withOverrides` moves an overridden sub-page to the end of the plugin's
// extensions array, which changes the order the parent `page:user-settings`
// extension receives its `pages` input in — and that order picks the default
// tab. Reorder `inputs.pages` explicitly on the parent to keep "General" first.
const settingsPagesOrder = ['general', 'auth-providers', 'feature-flags'];

export const customUserSettingsPlugin: typeof userSettingsPlugin =
  userSettingsPlugin.withOverrides({
    extensions: [
      userSettingsPlugin
        .getExtension('sub-page:user-settings/general')
        .override({
          factory(originalFactory) {
            return originalFactory({
              params: {
                loader: async () => {
                  const {
                    UserSettingsAppearanceCard,
                    UserSettingsProfileCard,
                  } = await import('@backstage/plugin-user-settings');
                  const { UserSettingsFilteredIdentityCard } = await import(
                    '../components/settings/UserSettingsFilteredIdentityCard'
                  );
                  const { OktaGroupsCard } = await import(
                    '../components/settings/OktaGroupsCard'
                  );

                  return (
                    <Content>
                      <Grid.Root columns={{ initial: '1', md: '2' }} gap="6">
                        <Grid.Item>
                          <UserSettingsProfileCard />
                        </Grid.Item>
                        <Grid.Item>
                          <UserSettingsAppearanceCard />
                        </Grid.Item>
                        <Grid.Item>
                          <UserSettingsFilteredIdentityCard />
                        </Grid.Item>
                        <Grid.Item>
                          <OktaGroupsCard />
                        </Grid.Item>
                      </Grid.Root>
                    </Content>
                  );
                },
              },
            });
          },
        }),
      // The custom reordering of tabs can be removed if https://github.com/backstage/backstage/issues/34449 is resolved
      userSettingsPlugin.getExtension('page:user-settings').override({
        factory(originalFactory, { inputs }) {
          return originalFactory({
            inputs: {
              pages: [...inputs.pages].sort(
                (a, b) =>
                  settingsPagesOrder.indexOf(
                    a.get(coreExtensionData.routePath),
                  ) -
                  settingsPagesOrder.indexOf(
                    b.get(coreExtensionData.routePath),
                  ),
              ),
            },
          });
        },
      }),
    ],
  });
